import * as React from 'react';
import Autosuggest from 'react-autosuggest';
import { fetchAutocomplete } from '../../actions/utility-actions';
import { Countries } from './countries';
import { TimeZones } from './timezones';

// #region Component Props

export type LOCALDATA_TYPES = 'timezones' | 'countries';

interface IProps {
    id                      : string;
    onSelectedCallback      : any;
    currentValue            : string;
    placeholder             : string;
    disabled                : boolean;
    classList?              : string;
    dataSource              : LOCALDATA_TYPES;
}

// #endregion

export class LocalDataAutoComplete extends React.Component<IProps, any> {

    private theme = {
        containerOpen:            'autocomplete__container--open',
        container:                'autocomplete__container',
        input:                    'autocomplete__input',
        inputOpen:                'autocomplete__input--open',
        inputFocused:             'autocomplete__input--focused',
        suggestionsContainer:     'autocomplete__suggestions-container',
        suggestionsContainerOpen: 'autocomplete__suggestions-container--open',
        suggestionsList:          'autocomplete__suggestions-list',
        suggestion:               'autocomplete__suggestion',
        suggestionFirst:          'autocomplete__suggestion--first',
        suggestionHighlighted:    'autocomplete__suggestion--highlighted',
        sectionContainer:         'autocomplete__section-container',
        sectionContainerFirst:    'autocomplete__section-container--first',
        sectionTitle:             'autocomplete__section-title'
    };

    private theInput = {} as any;

    constructor(props : IProps) {
        super(props);

        this.state = {
            val: this.props.currentValue ? this.props.currentValue : '',
            suggestions: [],
            isFetching: false,
            hadInitialState: this.props.currentValue ? true : false,
            initialStateCopy: this.props.currentValue ? this.props.currentValue : null,
            hadSelection: false
        };    
    }

    /**
     * Add the onKeyDown event listenr if it exists for capturing the 
     * enter keycode.
     */
    public componentDidMount() {
        if (this.theInput && this.theInput.input) {
            this.theInput.input.addEventListener('blur', this.onBlur);
        }
    }

    /**
     * Remove the onKeyDown event listener if it exists
     */
    public componentWillUnmount() {
        if (this.theInput && this.theInput.input) {
            this.theInput.input.removeEventListener('blur', this.onBlur);
        }
    }

    /**
     * Handle re-hydrating the state of the component
     */
    public UNSAFE_componentWillReceiveProps(newProps: any) {
        if (newProps.currentValue !== this.props.currentValue) {
            this.setState({
                val: newProps.currentValue
            });
        }
    }
    
    public render() {

        const { val, suggestions}: any = this.state;
        const classes = "form-control " + this.props.classList;

        const inputProps = {
            placeholder: this.props.placeholder,
            value: val ?? '',
            onChange: this.onChange,
            className: classes,
            spellCheck: false
        };

        const disableClass = this.props.disabled ? 'disable' : '';

        return <div className={"autocomplete__loading-wrapper " + disableClass}>

                <div className={"autocomplete__three-dots " + (this.state.isFetching ? 'show' : '')}>
                    <div className="dot1" />
                    <div className="dot2" />
                    <div className="dot3" />
                </div>

                <Autosuggest 
                    suggestions={suggestions}
                    onSuggestionsFetchRequested={this.onFetch}
                    onSuggestionsClearRequested={this.onClear}
                    ref={(instance: any) => { this.theInput = instance; }}   
                    onSuggestionSelected={this.onSuggestionSelected}
                    getSuggestionValue={this.getSuggestionValue}
                    renderSuggestion={this.renderSuggestion}
                    inputProps={inputProps}
                    theme={this.theme} />
     
        </div>;
    }

    /**
     * OnKeyDown handler, used for trapping enter.
     */
    private onBlur = (e: any) => {

        if (this.state.val === undefined ||
            this.state.val === null ||
            this.state.val.length < 1) {
                this.setState({
                    initialStateCopy: '',
                    hadInitialState: false
                });

                this.props.onSelectedCallback(null);
                this.onClear();
        }
        else if (this.state.hadInitialState && !this.state.hadSelection) {
            this.setState({
                val: this.state.initialStateCopy
            });
        }
        else if (!this.state.hadInitialState && 
                this.state.val && 
                this.state.val.length > 0 && 
                !this.state.hadSelection) {

            setTimeout(() => {
                this.setState({
                    val: this.state.initialStateCopy
                });
            }, 350);
        }

    }

    /**
     * Called when the component's value changes
     */
    private onChange = (event: any, { newValue, method }: any) => {
        if (newValue !== undefined && newValue !== null) {
            this.setState({
                val: newValue,
                hadSelection: false
            });
        }
    }

    /**
     * Used for fetching suggestions from the dealers API
     */
    private onFetch = ({ value, reason }: any) => {        
        if (reason !== 'force-search' && (reason === 'input-focused' || value.length === 0)) {
            return;
        }

        this.setState({
            isFetching: true
        });

        let ds : any = [];
        switch (this.props.dataSource) {
            case 'timezones':
                ds = TimeZones;
                break;
            case 'countries':
                ds = Countries;
                break;
        }

        const data = ds.filter((itm : any) => {
            return itm.display.toLowerCase().includes(value.toLowerCase());
        });

        if (!data || data.length === 0) {
            data.push({
                id: '',
                display: "No Results Found"
            });
        }

        this.setState({
            suggestions: data,
            isFetching: false
        });
    
    }

    /**
     * Handler for when the user makes a selection
     */
    private onSuggestionSelected = (event: any, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method } 
        : any) => {

        if (suggestion.id !== undefined && suggestion.display !== 'No Results Found') {
            this.props.onSelectedCallback(suggestion);

            this.setState({
                hadSelection: true,
                initialStateCopy: suggestion.id,
                hadInitialState: false
            });

            this.onClear();

            // Sighs loudly.
            setTimeout(this.onClear, 350);
        }
    }

    /**
     * Handler for clearing the suggestion list
     */
    private onClear = () => {
        this.setState({
            suggestions: []
        });
    }

    /**
     * We show the dealer name for the suggestion value, 
     * so pull the name property from the response
     */
    private getSuggestionValue = (suggestion: any) => {
        return suggestion.id;
    }

    /**
     * Simple <span> wrapper around the dealer options
     */
    private renderSuggestion = (suggestion: any) => {
        return suggestion.id 
                ? <span>{ suggestion.display }</span>
                : <span>{ suggestion.name }</span>;        
    }

}
